import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';

/**
 * Presentational "thinking" indicator shown while a copilot message is pending a
 * WebSocket response. Renders three pulsing dots plus a phrase that rotates every
 * couple of seconds, with verbs tailored to the agent being awaited.
 *
 * The phrase pool merges agent-specific verbs with a large shared pool, then
 * shuffles and cycles through it without repeating until exhausted — so even a
 * long wait rarely shows the same phrase twice.
 */
@Component({
  selector: 'copilot-thinking-indicator',
  templateUrl: './copilot-thinking-indicator.component.html',
  styleUrls: ['./copilot-thinking-indicator.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CopilotThinkingIndicatorComponent implements OnInit, OnDestroy {

  /** The copilot action being awaited (suggestion, health, playbook, sentiment, ask). */
  @Input() action = '';
  /** A pool of its own, for work that must not say the Copilot is reading the conversation (the insight, RF-41). */
  @Input() phrases: string[] = [];

  phrase = '';

  /** i18n keys for generic phrases that apply to any agent — resolved via the translate pipe. */
  private static readonly SHARED_PHRASES: string[] = [
    'attendance.copilot.thinking.shared.readingConversation',
    'attendance.copilot.thinking.shared.analyzingAttendance',
    'attendance.copilot.thinking.shared.reviewingMessages',
    'attendance.copilot.thinking.shared.processingHistory',
    'attendance.copilot.thinking.shared.gatheringContext',
    'attendance.copilot.thinking.shared.connectingDots',
    'attendance.copilot.thinking.shared.crossReferencing',
    'attendance.copilot.thinking.shared.interpretingDialogue',
    'attendance.copilot.thinking.shared.assessingScenario',
    'attendance.copilot.thinking.shared.organizingIdeas',
    'attendance.copilot.thinking.shared.checkingDetails',
    'attendance.copilot.thinking.shared.thinkingCarefully',
    'attendance.copilot.thinking.shared.structuringResponse',
    'attendance.copilot.thinking.shared.finalizingAnalysis',
    'attendance.copilot.thinking.shared.almostThere',
  ];

  /** i18n keys for agent-specific phrases, merged on top of the shared pool. */
  private static readonly PHRASES: Record<string, string[]> = {
    suggestion: [
      'attendance.copilot.thinking.suggestion.understandingContext',
      'attendance.copilot.thinking.suggestion.craftingSuggestions',
      'attendance.copilot.thinking.suggestion.adjustingTone',
      'attendance.copilot.thinking.suggestion.findingBestApproach',
      'attendance.copilot.thinking.suggestion.thinkingHowToReply',
      'attendance.copilot.thinking.suggestion.refiningOptions',
      'attendance.copilot.thinking.suggestion.calibratingEmpathy',
      'attendance.copilot.thinking.suggestion.choosingWords',
      'attendance.copilot.thinking.suggestion.consideringAlternatives',
      'attendance.copilot.thinking.suggestion.polishingWording',
    ],
    health: [
      'attendance.copilot.thinking.health.assessingQuality',
      'attendance.copilot.thinking.health.calculatingIndicators',
      'attendance.copilot.thinking.health.measuringEngagement',
      'attendance.copilot.thinking.health.identifyingRisks',
      'attendance.copilot.thinking.health.checkingFrictionSignals',
      'attendance.copilot.thinking.health.analyzingPace',
      'attendance.copilot.thinking.health.checkingSatisfaction',
      'attendance.copilot.thinking.health.assessingResolution',
    ],
    playbook: [
      'attendance.copilot.thinking.playbook.reviewingPlaybook',
      'attendance.copilot.thinking.playbook.identifyingCurrentStep',
      'attendance.copilot.thinking.playbook.checkingAdherence',
      'attendance.copilot.thinking.playbook.mappingNextSteps',
      'attendance.copilot.thinking.playbook.comparingIdealProcess',
      'attendance.copilot.thinking.playbook.assessingSalesProgress',
      'attendance.copilot.thinking.playbook.checkingScript',
    ],
    sentiment: [
      'attendance.copilot.thinking.sentiment.interpretingSentiment',
      'attendance.copilot.thinking.sentiment.assessingCustomerTone',
      'attendance.copilot.thinking.sentiment.detectingEmotions',
      'attendance.copilot.thinking.sentiment.measuringConversationTemperature',
      'attendance.copilot.thinking.sentiment.trackingMoodEvolution',
      'attendance.copilot.thinking.sentiment.readingBetweenLines',
      'attendance.copilot.thinking.sentiment.assessingCustomerSatisfaction',
    ],
    ask: [
      'attendance.copilot.thinking.ask.searchingAnswer',
      'attendance.copilot.thinking.ask.formulatingAnswer',
      'attendance.copilot.thinking.ask.searchingHistory',
      'attendance.copilot.thinking.ask.findingInformation',
      'attendance.copilot.thinking.ask.verifyingFacts',
      'attendance.copilot.thinking.ask.assemblingExplanation',
    ],
  };

  private static readonly ROTATE_INTERVAL_MS = 2200;

  private pool: string[] = [];
  private index = 0;
  private timer?: ReturnType<typeof setInterval>;

  ngOnInit(): void {
    const specific = CopilotThinkingIndicatorComponent.PHRASES[this.action] ?? [];
    const merged = this.phrases.length
      ? this.phrases
      : Array.from(new Set([...specific, ...CopilotThinkingIndicatorComponent.SHARED_PHRASES]));

    this.pool = this.shuffle(merged);
    this.index = 0;
    this.phrase = this.pool[0] ?? 'attendance.copilot.thinking.shared.analyzingAttendance';

    if (this.pool.length > 1) {
      this.timer = setInterval(() => this.next(), CopilotThinkingIndicatorComponent.ROTATE_INTERVAL_MS);
    }
  }

  ngOnDestroy(): void {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  private next(): void {
    this.index += 1;
    if (this.index >= this.pool.length) {
      // Reshuffle for the next lap, avoiding an immediate repeat across the seam.
      const last = this.pool[this.pool.length - 1];
      this.pool = this.shuffle(this.pool);
      if (this.pool.length > 1 && this.pool[0] === last) {
        [this.pool[0], this.pool[1]] = [this.pool[1], this.pool[0]];
      }
      this.index = 0;
    }
    this.phrase = this.pool[this.index];
  }

  /** Fisher-Yates shuffle returning a new array. */
  private shuffle(source: string[]): string[] {
    const arr = [...source];
    for (let i = arr.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }
}
